import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CurrencyInput from "@/components/ui/currency-input";
import DateField from "@/components/ui/date-field";
import DateTimeField from "@/components/ui/date-time-field";
import DriverSelect from "@/components/ui/driver-select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import VehicleSelect from "@/components/ui/vehicle-select";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import type { Ticket } from "@/interfaces";
import { ticketsService } from "@/services/api/tickets";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect } from "react";
import { type DefaultValues, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import { TicketDetailsLoading } from "./TicketDetailsLoading";

const formSchema = z.object({
  amount: z
    .number({
      error: "Valor é obrigatório",
    })
    .min(0.01, "Valor deve ser maior que 0"),
  code: z
    .string({
      message: "Código da multa é obrigatório",
    })
    .min(1, "Código da multa é obrigatório"),
  due_date: z
    .string({
      message: "Data de vencimento é obrigatória",
    })
    .date("Data de vencimento inválida"),
  classification: z.string().min(1, "Classificação é obrigatória"),
  description: z.string().min(1, "Descrição é obrigatória"),
  local: z.string().min(1, "Local é obrigatório"),
  date_time: z.string().min(1, "Data e hora são obrigatórios"),
  driver_id: z.number().nullable(),
  vehicle_id: z
    .number({
      error: "Veículo é obrigatório",
    })
    .min(1, "Veículo é obrigatório"),
});

export type TicketFormValues = z.infer<typeof formSchema>;

const CLASSIFICATIONS = [
  { value: "Leve", label: "Leve (3 pontos)" },
  { value: "Média", label: "Média (4 pontos)" },
  { value: "Grave", label: "Grave (5 pontos)" },
  { value: "Gravíssima", label: "Gravíssima (7 pontos)" },
];

const createValues = (): DefaultValues<TicketFormValues> => ({
  amount: undefined,
  code: "",
  due_date: format(new Date(), "yyyy-MM-dd"),
  classification: "",
  description: "",
  local: "",
  date_time: "",
  driver_id: null,
  vehicle_id: undefined,
});

const editValues = (ticket: Ticket): DefaultValues<TicketFormValues> => ({
  amount: Number(ticket.amount) || 0,
  code: ticket.code || "",
  due_date: ticket.due_date || "",
  classification: ticket.classification || "",
  description: ticket.description || "",
  local: ticket.local || "",
  date_time: ticket.date_time ? ticket.date_time.substring(0, 16) : "",
  driver_id: ticket.driver_id ?? null,
  vehicle_id: Number(ticket.vehicle_id) || 0,
});

interface TicketFormContentProps {
  id?: string;
  initialValues: DefaultValues<TicketFormValues>;
}

const TicketFormContent = ({ id, initialValues }: TicketFormContentProps) => {
  const navigate = useNavigate();

  const form = useForm<TicketFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const { trigger } = form;

  // A ticket without a due date opens with the field already flagged, as the
  // old post-load reset did.
  useEffect(() => {
    if (id && !initialValues.due_date) {
      void trigger("due_date");
    }
  }, [id, initialValues.due_date, trigger]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: ticketsService.store,
    updateService: ticketsService.update,
    onSuccess: () => navigate("/tickets"),
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 ">
        <FormField
          control={form.control}
          name="vehicle_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Veículo</FormLabel>
              <VehicleSelect
                value={field.value ?? undefined}
                onChange={field.onChange}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="driver_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Condutor</FormLabel>
              <FormControl>
                <DriverSelect
                  value={field.value ?? undefined}
                  noun="condutor"
                  onChange={(value) =>
                    field.onChange(typeof value === "number" ? value : null)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Valor</FormLabel>
              <FormControl>
                <CurrencyInput
                  value={field.value}
                  onChange={(value) =>
                    field.onChange(value === null ? undefined : value)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Código da multa</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DateField
          form={form}
          label="Data de vencimento"
          name="due_date"
          placeholder="Selecione uma data"
        />

        <FormField
          control={form.control}
          name="classification"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Classificação da infração</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a classificação" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CLASSIFICATIONS.map((classification) => (
                    <SelectItem
                      key={classification.value}
                      value={classification.value}
                    >
                      {classification.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="local"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Local</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DateTimeField
          form={form}
          name="date_time"
          label="Data e hora da ocorrência"
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Descrição</FormLabel>
              <FormControl>
                <Textarea {...field} className="h-20 dark:border-white" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/tickets")}
          >
            Cancelar
          </Button>

          <SubmitButton loading={loading} />
        </div>
      </form>
    </Form>
  );
};

const TicketForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: ticket, error } = useQuery({
    queryKey: ["tickets", "detail", Number(id)],
    queryFn: () => ticketsService.show(Number(id)),
    enabled: Boolean(id),
    // The form reads the ticket only at mount: always open with fresh data.
    gcTime: 0,
    // A single attempt, as before the migration.
    retry: false,
  });

  useEffect(() => {
    // Only the initial load redirects; a failed background refetch must not
    // throw the user out of a form being edited.
    if (error && !ticket) {
      errorHandler(error, "Erro ao carregar multa");
      navigate("/tickets");
    }
  }, [error, ticket, navigate]);

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Multa" : "Nova Multa"}</CardTitle>
      </CardHeader>
      <CardContent>
        {!id ? (
          <TicketFormContent initialValues={createValues()} />
        ) : ticket ? (
          <TicketFormContent
            key={id}
            id={id}
            initialValues={editValues(ticket.data)}
          />
        ) : (
          <TicketDetailsLoading />
        )}
      </CardContent>
    </Card>
  );
};

export default TicketForm;
